export const preProcessTicketStatuses = [
  "JOB_REQUEST_AWAITING_APPROVAL",
  "JOB_REQUEST_AWAITING_QUOTE",
  "JOB_REQUEST_AWAITING_QUOTE_APPROVAL",
  "JOB_REQUEST_QUOTE_REJECTED",
  "JOB_REQUEST_ACCEPTED",
  "JOB_REQUEST_GETTING_READY",
] as const
export type PreProcessTicketStatus = (typeof preProcessTicketStatuses)[number]

export const workTicketStatuses = ["JOB_READY", "JOB_IN_PROGRESS", "JOB_AWAITING_MATERIAL", "JOB_LEFT_UNFINISHED"] as const
export type WorkTicketStatus = (typeof workTicketStatuses)[number]

export const postProcessTicketStatuses = [
  "JOB_FINISHED",
  "JOB_RESULT_AWAITING_INTERNAL_APPROVAL",
  "JOB_RESULT_AWAITING_EXTERNAL_APPROVAL",
  "JOB_RESULT_VISI",
  "JOB_RESULT_APPROVED",
  "JOB_RESULT_REJECTED",
  "JOB_RESULT_INVOICED",
  "JOB_RESULT_DONE",
] as const
export type PostProcessTicketStatus = (typeof postProcessTicketStatuses)[number]

export const cancelledTicketStatuses = ["JOB_RESULT_CANCELLED"] as const
export type CancelledTicketStatus = (typeof cancelledTicketStatuses)[number]

export const ticketStatuses = [
  ...preProcessTicketStatuses,
  ...workTicketStatuses,
  ...postProcessTicketStatuses,
  ...cancelledTicketStatuses,
] as const
export type TicketStatus = (typeof ticketStatuses)[number]

export enum TicketStatusType {
  JOB_REQUEST_AWAITING_APPROVAL = "JOB_REQUEST_AWAITING_APPROVAL",
  JOB_REQUEST_AWAITING_QUOTE = "JOB_REQUEST_AWAITING_QUOTE",
  JOB_REQUEST_AWAITING_QUOTE_APPROVAL = "JOB_REQUEST_AWAITING_QUOTE_APPROVAL",
  JOB_REQUEST_QUOTE_REJECTED = "JOB_REQUEST_QUOTE_REJECTED",
  JOB_REQUEST_ACCEPTED = "JOB_REQUEST_ACCEPTED",
  JOB_REQUEST_GETTING_READY = "JOB_REQUEST_GETTING_READY",
}

export enum TicketMovementCheckData {
  QUOTES = "quotes",
  TICKET = "ticket",
}

export type TicketMovementCheckDataMap = {
  [TicketMovementCheckData.QUOTES]?: Array<TicketQuote>
  [TicketMovementCheckData.TICKET]?: Ticket
}

export interface TicketStatusMovement {
  from: TicketStatus
  to: TicketStatus
  canPerformMovement?: OrganizationType // If not set, the movement is selectable by all organizations
  selectionField?: {
    name: string
    items: Array<string>
  }
  allowRemarkField?: boolean
}

/* This is the workflow used when the organization is also the supplier */
export const ticketStatusMovementsOrganization: Array<TicketStatusMovement> = [
  // Pre process phase
  {
    from: "JOB_REQUEST_AWAITING_APPROVAL",
    to: "JOB_READY",
  },
  {
    from: "JOB_REQUEST_AWAITING_QUOTE",
    to: "JOB_READY",
  },
  {
    from: "JOB_REQUEST_ACCEPTED",
    to: "JOB_READY",
  },
  {
    from: "JOB_REQUEST_GETTING_READY",
    to: "JOB_READY",
  },

  // Work phase
  {
    from: "JOB_READY",
    to: "JOB_IN_PROGRESS",
  },
  {
    from: "JOB_READY",
    to: "JOB_FINISHED",
  },
  {
    from: "JOB_READY",
    to: "JOB_LEFT_UNFINISHED",
    selectionField: {
      name: "UNFINISHED_REASON",
      items: ["NO_PHYSICAL_ACCESS", "NO_MATERIAL", "OTHER"],
    },
    allowRemarkField: true,
  },
  {
    from: "JOB_IN_PROGRESS",
    to: "JOB_FINISHED",
  },
  {
    from: "JOB_FINISHED",
    to: "JOB_IN_PROGRESS",
  },
  {
    from: "JOB_LEFT_UNFINISHED",
    to: "JOB_IN_PROGRESS",
  },
  {
    from: "JOB_IN_PROGRESS",
    to: "JOB_LEFT_UNFINISHED",
    selectionField: {
      name: "UNFINISHED_REASON",
      items: ["NO_PHYSICAL_ACCESS", "NO_MATERIAL", "OTHER"],
    },
    allowRemarkField: true,
  },
  {
    from: "JOB_LEFT_UNFINISHED",
    to: "JOB_READY",
    allowRemarkField: true,
  },
  {
    from: "JOB_FINISHED",
    to: "JOB_RESULT_DONE",
  },

  // Cancel ticket
  ...ticketStatuses
    .filter((status) => !["JOB_RESULT_CANCELLED", "JOB_RESULT_DONE"].includes(status))
    .map((status) => ({
      from: status,
      to: "JOB_RESULT_CANCELLED" as const,
      allowRemarkField: true,
    })),
]

/* This is the workflow used when the supplier is different from the organization */
export const ticketStatusMovementsSupplier: Array<TicketStatusMovement> = [
  // Pre process phase
  {
    from: "JOB_REQUEST_AWAITING_APPROVAL",
    to: "JOB_REQUEST_AWAITING_QUOTE",
    canPerformMovement: OrganizationType.CUSTOMER,
  },
  {
    from: "JOB_REQUEST_AWAITING_APPROVAL",
    to: "JOB_REQUEST_ACCEPTED",
    canPerformMovement: OrganizationType.CUSTOMER,
  },
  {
    from: "JOB_REQUEST_AWAITING_QUOTE",
    to: "JOB_REQUEST_AWAITING_QUOTE_APPROVAL",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_REQUEST_AWAITING_QUOTE_APPROVAL",
    to: "JOB_REQUEST_ACCEPTED",
    canPerformMovement: OrganizationType.CUSTOMER,
  },
  {
    from: "JOB_REQUEST_AWAITING_QUOTE_APPROVAL",
    to: "JOB_REQUEST_QUOTE_REJECTED",
    canPerformMovement: OrganizationType.CUSTOMER,
    allowRemarkField: true,
  },
  {
    from: "JOB_REQUEST_AWAITING_QUOTE_APPROVAL",
    to: "JOB_REQUEST_AWAITING_QUOTE",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_REQUEST_QUOTE_REJECTED",
    to: "JOB_REQUEST_AWAITING_QUOTE",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_REQUEST_QUOTE_REJECTED",
    to: "JOB_REQUEST_AWAITING_QUOTE_APPROVAL",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_REQUEST_ACCEPTED",
    to: "JOB_REQUEST_GETTING_READY",
    canPerformMovement: OrganizationType.SUPPLIER,
    selectionField: {
      name: "GETTING_READY_REASON",
      items: ["ORDERING_MATERIAL", "REQUIRES_THIRD_PARTY_SCHEDULING", "AWAIT_CLEANING", "OTHER"],
    },
    allowRemarkField: true,
  },
  {
    from: "JOB_REQUEST_ACCEPTED",
    to: "JOB_READY",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_REQUEST_GETTING_READY",
    to: "JOB_READY",
    canPerformMovement: OrganizationType.SUPPLIER,
  },

  // Work phase
  {
    from: "JOB_READY",
    to: "JOB_IN_PROGRESS",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_READY",
    to: "JOB_AWAITING_MATERIAL",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_AWAITING_MATERIAL",
    to: "JOB_IN_PROGRESS",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_READY",
    to: "JOB_FINISHED",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_READY",
    to: "JOB_LEFT_UNFINISHED",
    canPerformMovement: OrganizationType.SUPPLIER,
    selectionField: {
      name: "UNFINISHED_REASON",
      items: ["NO_PHYSICAL_ACCESS", "NO_MATERIAL", "OTHER"],
    },
    allowRemarkField: true,
  },
  {
    from: "JOB_IN_PROGRESS",
    to: "JOB_LEFT_UNFINISHED",
    canPerformMovement: OrganizationType.SUPPLIER,
    selectionField: {
      name: "UNFINISHED_REASON",
      items: ["NO_PHYSICAL_ACCESS", "NO_MATERIAL", "OTHER"],
    },
    allowRemarkField: true,
  },
  {
    from: "JOB_LEFT_UNFINISHED",
    to: "JOB_IN_PROGRESS",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_LEFT_UNFINISHED",
    to: "JOB_READY",
    canPerformMovement: OrganizationType.SUPPLIER,
    allowRemarkField: true,
  },
  {
    from: "JOB_IN_PROGRESS",
    to: "JOB_FINISHED",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_FINISHED",
    to: "JOB_IN_PROGRESS",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_FINISHED",
    to: "JOB_RESULT_AWAITING_INTERNAL_APPROVAL",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_FINISHED",
    to: "JOB_RESULT_AWAITING_EXTERNAL_APPROVAL",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_FINISHED",
    to: "JOB_RESULT_DONE",
  },

  // Post process phase
  {
    from: "JOB_RESULT_AWAITING_INTERNAL_APPROVAL",
    to: "JOB_RESULT_AWAITING_EXTERNAL_APPROVAL",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_RESULT_AWAITING_EXTERNAL_APPROVAL",
    to: "JOB_RESULT_APPROVED",
    canPerformMovement: OrganizationType.CUSTOMER,
  },
  {
    from: "JOB_RESULT_AWAITING_EXTERNAL_APPROVAL",
    to: "JOB_RESULT_REJECTED",
    canPerformMovement: OrganizationType.CUSTOMER,
    selectionField: {
      name: "REJECTED_REASON",
      items: ["INCORRECT_ARTICLES", "WORK_NOT_ACCEPTABLE", "WORK_NOT_FINISHED", "OTHER"],
    },
    allowRemarkField: true,
  },
  {
    from: "JOB_RESULT_APPROVED",
    to: "JOB_RESULT_INVOICED",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_RESULT_APPROVED",
    to: "JOB_RESULT_DONE",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_RESULT_INVOICED",
    to: "JOB_RESULT_DONE",
    canPerformMovement: OrganizationType.SUPPLIER,
  },
  {
    from: "JOB_RESULT_REJECTED",
    to: "JOB_RESULT_AWAITING_EXTERNAL_APPROVAL",
    canPerformMovement: OrganizationType.SUPPLIER,
    allowRemarkField: true,
  },

  // Cancel ticket
  ...ticketStatuses
    .filter((status) => !["JOB_RESULT_CANCELLED", "JOB_RESULT_DONE"].includes(status))
    .map((status) => ({
      from: status,
      to: "JOB_RESULT_CANCELLED" as const,
      allowRemarkField: true,
    })),
]

export const ticketStatusColors: Record<TicketStatus, string> = {
  JOB_REQUEST_AWAITING_APPROVAL: "#FDD835",
  JOB_REQUEST_AWAITING_QUOTE: "#FDD835",
  JOB_REQUEST_AWAITING_QUOTE_APPROVAL: "#FDD835",
  JOB_REQUEST_QUOTE_REJECTED: "#C10015",
  JOB_REQUEST_ACCEPTED: "#FDD835",
  JOB_REQUEST_GETTING_READY: "#FDD835",
  JOB_READY: "#FF9800",
  JOB_AWAITING_MATERIAL: "#FF9800",
  JOB_IN_PROGRESS: "#FF9800",
  JOB_LEFT_UNFINISHED: "#EF6C00",
  JOB_FINISHED: "#4CAF50",
  JOB_RESULT_AWAITING_INTERNAL_APPROVAL: "#9C27B0",
  JOB_RESULT_AWAITING_EXTERNAL_APPROVAL: "#9C27B0",
  JOB_RESULT_VISI: "#9C27B0",
  JOB_RESULT_APPROVED: "#9C27B0",
  JOB_RESULT_REJECTED: "#9C27B0",
  JOB_RESULT_INVOICED: "#9C27B0",
  JOB_RESULT_DONE: "#0989C0FF",
  JOB_RESULT_CANCELLED: "#C10015",
}

export const ticketCreationStatuses = [
  /**
   * A supplier may create a ticket that requires the approval of the organization.
   */
  "JOB_REQUEST_AWAITING_APPROVAL",
  /**
   * An organization may create a ticket that requires the quote of the supplier.
   */
  "JOB_REQUEST_AWAITING_QUOTE",
  /**
   * An organization may create a ticket that's accepted, and ready to be picked up by the supplier.
   */
  "JOB_REQUEST_ACCEPTED",
  /**
   * A supplier may create a ticket that's accepted, and ready to be picked up, but requires some preparation.
   */
  "JOB_REQUEST_GETTING_READY",
  /**
   * - A supplier may create a ticket that's accepted and ready to be picked up.
   * - An organization may create a ticket that's accepted and ready to be picked up.
   */
  "JOB_READY",
] as const satisfies Array<TicketStatus>
export type TicketCreationStatus = (typeof ticketCreationStatuses)[number]
